import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Plus, Truck, Mail, Phone, Pencil, CheckCircle2, XCircle, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ConfirmDialog } from "@/components/ConfirmDialog";

const field = "mt-1 w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-[#0B192C]";
const lbl = "text-xs font-semibold uppercase tracking-wider text-slate-500";
const EMPTY = { name: "", email: "", password: "", phone: "", employee_id: "", vehicle: "", registration: "", role: "driver", active: true };

export default function Drivers() {
  const qc = useQueryClient();
  const [edit, setEdit] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const { data: drivers = [], isLoading } = useQuery({ queryKey: ["drivers"], queryFn: async () => (await api.get("/drivers")).data });

  const del = (d) => setConfirm({
    title: `Delete driver ${d.name}?`,
    description: "This permanently removes the driver's login and profile. Drivers with active deliveries can't be deleted — reassign those first.",
    confirmLabel: "Delete", danger: true,
    onConfirm: async () => {
      try { await api.delete(`/users/${d.id}`); qc.invalidateQueries({ queryKey: ["drivers"] }); toast.success("Driver deleted"); }
      catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    },
  });

  return (
    <div className="animate-slide-up">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div><h1 className="font-head text-2xl font-extrabold text-[#0B192C]">Drivers</h1><p className="text-sm text-slate-500">{drivers.length} drivers</p></div>
        <button onClick={() => setEdit(EMPTY)} data-testid="add-driver-btn" className="flex items-center gap-2 rounded-xl bg-[#FF6500] px-4 py-2.5 text-sm font-bold text-white tap-shrink hover:bg-[#E05A00]"><Plus size={18} /> Add Driver</button>
      </div>

      {isLoading ? <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#FF6500]" /></div> : (
        <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
          {drivers.map((d) => (
            <div key={d.id} className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={`driver-card-${d.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="flex h-11 w-11 items-center justify-center rounded-full bg-[#0B192C] font-bold text-white">{d.name?.[0]}</div>
                  <div><div className="font-head font-bold text-[#0B192C]">{d.name}</div><div className="text-xs text-slate-400">{d.employee_id || "—"}</div></div>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => setEdit({ ...d, password: "" })} data-testid={`edit-driver-${d.id}`} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100"><Pencil size={15} /></button>
                  <button onClick={() => del(d)} data-testid={`delete-driver-${d.id}`} className="rounded-lg p-1.5 text-rose-500 hover:bg-rose-50"><Trash2 size={15} /></button>
                </div>
              </div>
              <div className="mt-3 space-y-1 text-sm text-slate-500">
                <div className="flex items-center gap-1.5"><Mail size={14} /> {d.email}</div>
                <div className="flex items-center gap-1.5"><Phone size={14} /> {d.phone || "—"}</div>
                <div className="flex items-center gap-1.5"><Truck size={14} /> {d.vehicle || "—"} {d.registration && `· ${d.registration}`}</div>
              </div>
              <div className="mt-2">
                {d.active !== false ? <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-700"><CheckCircle2 size={12} /> Active</span>
                  : <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-500"><XCircle size={12} /> Inactive</span>}
              </div>
            </div>
          ))}
        </div>
      )}

      {edit && <DriverDialog initial={edit} onClose={() => setEdit(null)} onDone={() => { setEdit(null); qc.invalidateQueries({ queryKey: ["drivers"] }); }} />}
      <ConfirmDialog confirm={confirm} onClose={() => setConfirm(null)} />
    </div>
  );
}

function DriverDialog({ initial, onClose, onDone }) {
  const [f, setF] = useState(initial);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF({ ...f, [k]: v });
  const isEdit = !!f.id;

  const save = async () => {
    if (!f.name || !f.email) return toast.error("Name and email required");
    if (!isEdit && !f.password) return toast.error("Password required for new driver");
    setSaving(true);
    try {
      if (isEdit) {
        const payload = { name: f.name, phone: f.phone, employee_id: f.employee_id, vehicle: f.vehicle, registration: f.registration, active: f.active };
        if (f.password) payload.password = f.password;
        await api.put(`/users/${f.id}`, payload);
      } else {
        await api.post("/users", { ...f, role: "driver" });
      }
      toast.success(`Driver ${isEdit ? "updated" : "created"}`);
      onDone();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto scrollbar-thin" data-testid="driver-dialog">
        <DialogHeader><DialogTitle>{isEdit ? "Edit" : "Add"} Driver</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><label className={lbl}>Full Name *</label><input data-testid="driver-name-input" value={f.name} onChange={(e) => set("name", e.target.value)} className={field} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={lbl}>Email *</label><input data-testid="driver-email-input" type="email" disabled={isEdit} value={f.email} onChange={(e) => set("email", e.target.value)} className={`${field} disabled:bg-slate-100`} /></div>
            <div><label className={lbl}>{isEdit ? "New Password" : "Password *"}</label><input data-testid="driver-password-input" type="text" value={f.password} onChange={(e) => set("password", e.target.value)} className={field} placeholder={isEdit ? "leave blank to keep" : ""} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={lbl}>Mobile</label><input value={f.phone} onChange={(e) => set("phone", e.target.value)} className={field} /></div>
            <div><label className={lbl}>Driver ID</label><input value={f.employee_id} onChange={(e) => set("employee_id", e.target.value)} className={field} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={lbl}>Vehicle</label><input value={f.vehicle} onChange={(e) => set("vehicle", e.target.value)} className={field} /></div>
            <div><label className={lbl}>Registration</label><input value={f.registration} onChange={(e) => set("registration", e.target.value)} className={field} /></div>
          </div>
          {isEdit && (
            <label className="flex items-center gap-2 text-sm font-medium text-slate-600">
              <input type="checkbox" checked={f.active !== false} onChange={(e) => set("active", e.target.checked)} /> Active
            </label>
          )}
          <button onClick={save} disabled={saving} data-testid="save-driver-btn" className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#0B192C] py-3 font-bold text-white tap-shrink disabled:opacity-60">
            {saving ? <Loader2 className="animate-spin" size={18} /> : "Save Driver"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
